import { SpotterSystemError } from '@/api/devices';
import { LEARN_MORE_URL_CELL_NOT_WORKING } from '@/components/spotter/TelemetryServiceStatusMessage';
import {
    InlineAlert,
    InlineAlertBody,
    InlineAlertPrimaryButton,
    InlineAlertSecondaryButton,
    InlineAlertTitle,
} from '@/components/ui/InlineAlert';
import { contactSupportHref } from '@/utils/urlHelpers';

export const INSUFFICIENT_SIGNAL_ERRORS = ['ERR_NO_CONNECTION', 'ERR_TX_ERR', 'ERR_MISC_ERR'] as const;

export type CellularAlert = {
    actionButton: (spotterId: string) => JSX.Element;
    title: string;
    body: string;
};

export const CELLULAR_ALERT_MESSAGING = {
    cellMemoryFull: {
        title: 'Inconsistent signal',
        body: 'Your device may not be able to transmit via cellular telemetry. Contact Support to troubleshoot this issue.',
    },
    insufficientSignal: {
        title: 'Insufficient signal',
        body: 'Your device may not be able to transmit via cellular telemetry. Try moving your device or turn off HDR in Settings to prioritize consistent transmissions of embedded data only.',
    },
    initError: {
        title: 'Cellular modem error',
        body: 'Your cellular modem failed to initialize. Please retrieve your device and refer to support documentation to troubleshoot.',
    },
};

export const CELLULAR_ALERTS: Record<string, CellularAlert> = {
    cellMemoryFull: {
        actionButton: (spotterId: string) => (
            <InlineAlertPrimaryButton
                href={contactSupportHref({
                    subject: `Spotter cell problem: ${spotterId}`,
                    body: `Hi Spotter support, ${spotterId} has a Cellular memory full error and is not transmitting over cellular.\n\n`,
                })}
            >
                Contact Support
            </InlineAlertPrimaryButton>
        ),
        title: CELLULAR_ALERT_MESSAGING.cellMemoryFull.title,
        body: CELLULAR_ALERT_MESSAGING.cellMemoryFull.body,
    },
    insufficientSignal: {
        actionButton: (spotterId: string) => (
            <>
                <InlineAlertSecondaryButton href={`/spotter/${spotterId}#configuration`}>
                    Go to settings
                </InlineAlertSecondaryButton>
                <InlineAlertPrimaryButton href={LEARN_MORE_URL_CELL_NOT_WORKING}>Learn more</InlineAlertPrimaryButton>
            </>
        ),
        title: CELLULAR_ALERT_MESSAGING.insufficientSignal.title,
        body: CELLULAR_ALERT_MESSAGING.insufficientSignal.body,
    },
    initError: {
        actionButton: (spotterId: string) => (
            <>
                <InlineAlertSecondaryButton
                    href={contactSupportHref({
                        subject: `Spotter cell problem: ${spotterId}`,
                        body: `Hi Spotter support, ${spotterId} has a Cellular modem initialization error and is not transmitting over cellular.\n\n`,
                    })}
                >
                    Contact Support
                </InlineAlertSecondaryButton>
                <InlineAlertPrimaryButton href={LEARN_MORE_URL_CELL_NOT_WORKING}>Learn more</InlineAlertPrimaryButton>
            </>
        ),
        title: CELLULAR_ALERT_MESSAGING.initError.title,
        body: CELLULAR_ALERT_MESSAGING.initError.body,
    },
} as const;

export const CellularErrorAlert = ({ error, spotterId }: { error: SpotterSystemError; spotterId: string | null }) => {
    let alert: CellularAlert;

    if (error.code === 'WARN_CELL_MEMORY_FULL') {
        alert = CELLULAR_ALERTS.cellMemoryFull;
    } else if (INSUFFICIENT_SIGNAL_ERRORS.some((errorCode) => errorCode === error.code)) {
        alert = CELLULAR_ALERTS.insufficientSignal;
    } else {
        alert = CELLULAR_ALERTS.initError;
    }

    if (!spotterId) {
        return null;
    }

    return (
        <InlineAlert severity="error" actionButton={alert.actionButton(spotterId)} dismissNotificationId={error.code}>
            <InlineAlertTitle>{alert.title}</InlineAlertTitle>
            <InlineAlertBody>{alert.body}</InlineAlertBody>
        </InlineAlert>
    );
};
